/* dashboard/vidi_feed.js — Vidi v2 node-persistent feed renderer.
 *
 * Replaces the Alpine `x-for` that used to render the vidiprinter. That
 * x-for re-rendered the whole list on every feed change, so a new event
 * just popped in place (a glow faked "movement"). This renderer OWNS its
 * DOM nodes and animates the diff, giving real motion:
 *
 *   enter → the card drops in from just above (opacity + translateY) with
 *           an emerald fresh-ring that blooms and decays
 *   keep  → FLIP push-down: measure old pos → invert → spring to new pos,
 *           so cards physically slide down to make room
 *   exit  → the oldest lifts out of flow (absolute) and falls off the
 *           bottom, so the list never jumps
 *
 * Two lanes (Xav 2026-07-05, see memory project_vidi_v2_motion):
 *   wire    (60%) — every event, terse one-line rows, fills-to-height
 *   moments (40%) — key events only (event_type achievement/big_play),
 *                   richer cards that linger, with game-context line
 *
 * Motion validated in the scratchpad prototype before porting. The Lane
 * class is framework-agnostic; live.js mounts one VidiFeed per dashboard
 * and calls render() from an x-effect on the event stream.
 *
 * Public API:
 *   const vf = new VidiFeed({ helpers })   // helpers: {logoFor, descClean}
 *   vf.mount(wireEl, momentsEl)            // bind (or rebind) containers
 *   vf.render({ wire: [...], moments: [...] })   // both newest-first
 *   vf.clear()
 */
(function () {
  'use strict';

  const REDUCE = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // A fifth quarter is overtime on every Wire row ("Q5 11:02" - v2 run 2, LIVE-5).
  const otTime = (t) => String(t || '').replace(/^Q([5-9])/, (_, q) => (q === '5' ? 'OT' : (q - 4) + 'OT'));

  // Approx row heights — used only to derive fill-to-height capacity.
  const WIRE_ROW = 54;   // two-line event card
  const MOMENT_ROW = 62;

  // Burst cascade: per-card entrance delay when several cards enter in one
  // render, capped so a large spike still settles in well under a second.
  const STAGGER_MS = 85;
  const STAGGER_CAP_MS = 550;

  // A real minus (U+2212), never a hyphen: "-4.60" printed a dash beside a
  // number while the hero and the overlays print "−" (run 8, TYPE-15). A value
  // that rounds to zero carries no sign at all.
  const _num = (n, dec) => { const s = Math.abs(n).toFixed(dec); return (n < 0 && +s !== 0 ? '−' : '') + s; };
  const _signed = (n, dec) => { const s = _num(n, dec); return s.charAt(0) === '−' ? s : '+' + s; };
  const esc = (s) => (s == null ? '' : String(s)).replace(/[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* ── A single lane: a container the renderer owns, plus the enter /
   *    FLIP / exit choreography. Keyed by event id. ──────────────────── */
  class Lane {
    constructor(el, build, rowH) {
      this.el = el;                 // the .vidi-lane-body container
      this.build = build;           // (ev) => HTMLElement
      this.rowH = rowH;
      this.nodes = new Map();       // id -> node (insertion-persistent)
      this._cap = null;             // cached capacity - see below
    }

    /* capacity() is called on EVERY render and read clientHeight, which forces
     * a synchronous layout - on the demo that was the biggest single scripting
     * cost on a phone (mobile audit 2026-09-12). The lane only changes size
     * when its box does, and the renderer already watches for that
     * (_observe below), so the cache is cleared from there. */
    capacity() {
      if (this._cap != null) return this._cap;
      const h = this.el ? this.el.clientHeight : 0;
      // A lane laid out as a grid of cards (Broadcast's Big Moments, Xav
      // 2026-10-10) holds rows x columns, each row at least its min height.
      const cs = h ? getComputedStyle(this.el) : null;
      // A lane that scrolls (Broadcast's Wire) holds the whole run: a tall
      // play card that did not fit left the rest of the lane empty under the
      // one row before it (audit v2 run 3, WIRE-9)
      if (cs && (cs.overflowY === 'auto' || cs.overflowY === 'scroll') && cs.display !== 'grid') { this._cap = 40; return this._cap; }
      // A lane that runs sideways (Broadcast's Big Moments, one row that
      // slides) holds every card it is given - the pager shows them a page
      // at a time.
      if (cs && cs.display === 'flex' && cs.flexDirection === 'row') { this._cap = 30; return this._cap; }
      if (cs && cs.display === 'grid') {
        const cols = (cs.gridTemplateColumns || '').split(' ').filter(Boolean).length || 1;
        const gap = parseFloat(cs.rowGap) || 0;
        const minRow = parseFloat((cs.gridAutoRows || '').replace(/^minmax\(/, '')) || this.rowH;
        this._cap = Math.max(cols, cols * Math.floor((h + gap) / (minRow + gap)));
        return this._cap;
      }
      // a few more than the average height says: rows vary (a one-line row is
      // half a three-line one), and the tail that does not fit is hidden whole
      // by _clipTail - asking for the average left a half-empty Wire under two
      // tall rows (audit v2 run 3, WIRE-9)
      this._cap = h ? Math.max(2, Math.floor(h / this.rowH) + 4) : 12;
      return this._cap;
    }

    // events: array newest-first, already sliced to capacity by the caller.
    // instant=true forces a no-animation paint (same as REDUCE) — used for the
    // historical seed / pre-first-live-event renders so old rows don't drop in.
    render(events, instant) {
      if (!this.el) return;
      const noAnim = REDUCE || instant;
      const desired = events || [];
      const desiredIds = new Set(desired.map((e) => e.id));
      const surviving = desired.filter((e) => this.nodes.has(e.id));

      // 1. FIRST — record positions of the nodes that will survive
      const first = new Map();
      // (left too: in a grid of cards a card moves across as well as down)
      for (const e of surviving) { const n = this.nodes.get(e.id); first.set(e.id, [n.offsetLeft, n.offsetTop]); }

      // 2. EXIT — anything no longer wanted lifts out of flow + falls off
      for (const [id, node] of [...this.nodes]) {
        if (!desiredIds.has(id)) { this._exit(node); this.nodes.delete(id); }
      }

      // 3. Create enters + set DOM to desired order (appendChild reorders
      //    existing nodes in place, so one pass gives newest-first order).
      const enters = [];
      for (const ev of desired) {
        let node = this.nodes.get(ev.id);
        if (!node) {
          node = this.build(ev);
          node.dataset.evid = ev.id;
          if (!noAnim) { node.style.opacity = '0'; node.style.transform = 'translateY(-12px)'; }
          this.nodes.set(ev.id, node);
          enters.push(node);
        }
        this.el.appendChild(node);
      }

      // 4. LAST — FLIP survivors from their old position to the new one
      if (!noAnim) {
        for (const e of surviving) {
          const node = this.nodes.get(e.id);
          const [l0, t0] = first.get(e.id);
          const dx = l0 - node.offsetLeft, dy = t0 - node.offsetTop;
          if (dx || dy) this._flip(node, dy, dx);
        }
      }

      // 5. Play the enters. When a whole batch lands at once (the pbp layer
      //    ships the poll's accumulated at-bats in bulk — it bypasses the
      //    scorer drip), reveal them one-by-one so the lane cascades top-to-
      //    bottom instead of flickering the whole block into place in a single
      //    frame. `enters` is newest-first, so the newest reveals first and the
      //    rest ripple down beneath it. Stagger is capped so a big spike still
      //    settles quickly.
      enters.forEach((node, i) => this._enter(node, i, noAnim));
      // A sideways lane paged along to older plays goes back to the start
      // when a new one lands, so the newest is the one in view.
      // (after layout: scroll anchoring moves scrollLeft to keep the old
      // first card in view when one is put in front of it)
      if (enters.length && this.el.scrollWidth > this.el.clientWidth) {
        const el = this.el;
        requestAnimationFrame(() => { if (el.scrollLeft) el.scrollTo({ left: 0, behavior: noAnim ? 'auto' : 'smooth' }); });
      }
      this._clipTail();
    }

    /* Whole cards only. Capacity is a row-height estimate, so the last card
     * used to hang off the bottom edge half-drawn (Broadcast audit run 1).
     * A card that does not fit completely is hidden (kept in flow, so the
     * FLIP positions above it are untouched). Only in a clipping lane - the
     * phone shell's lane grows with its cards and caps them in CSS. */
    _clipTail() {
      const el = this.el;
      if (!el) return;
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') {           // it scrolls: every row is reachable
        for (const node of this.nodes.values()) node.style.visibility = '';
        this.shown = this.nodes.size;
        return;
      }
      if (oy === 'visible') return;
      const h = el.clientHeight;
      if (!h) return;
      // Learn the real card height: the 54px estimate was 69-85px on the
      // Broadcast Wire, so more cards were asked for than fit and the
      // hidden tail left a blank band (v2 run 2, SPACE-1). Capacity follows.
      const hs = [...this.nodes.values()].map(n => n.offsetHeight).filter(Boolean);
      if (hs.length >= 3) {
        const avg = hs.reduce((a, b) => a + b, 0) / hs.length + 6;   // + the gap
        if (Math.abs(avg - this.rowH) > 4) { this.rowH = avg; this._cap = null; }
      }
      let shown = 0;
      for (const node of this.nodes.values()) {
        const out = node.offsetTop + node.offsetHeight > h + 1;
        node.style.visibility = out ? 'hidden' : '';
        if (!out) shown++;
      }
      this.shown = shown;
    }

    _enter(node, idx, noAnim) {
      if (REDUCE || noAnim) { node.style.opacity = '1'; node.style.transform = 'none'; return; }
      const delay = Math.min((idx || 0) * STAGGER_MS, STAGGER_CAP_MS);
      const play = () => {
        if (node.__vfExiting) return;                  // left before it arrived
        // it shows once the rows under it have slid out of its way (the push
        // below is .3s): faded in over the sliding rows, two cards overlapped
        // in 2-4 of 60 samples (audit v2 run 3, WIRE-10)
        node.style.transition = 'opacity .22s ease-out .26s, transform .48s var(--vidi-ease-spring)';
        node.style.opacity = '1';
        node.style.transform = 'none';
        node.classList.add('is-fresh');
        setTimeout(() => node.classList.remove('is-fresh'), 1300);
      };
      if (delay) setTimeout(() => requestAnimationFrame(play), delay);
      else requestAnimationFrame(play);
      node.addEventListener('transitionend', function h(e) {
        if (e.propertyName === 'transform' && !node.__vfExiting) { node.style.transition = ''; node.removeEventListener('transitionend', h); }
      });
    }

    _flip(node, dy, dx) {
      node.style.transition = 'none';
      node.style.transform = 'translate(' + (dx || 0) + 'px, ' + dy + 'px)';
      void node.offsetHeight;                         // commit the invert
      requestAnimationFrame(() => {
        node.style.transition = 'transform .3s var(--vidi-ease-push)';
        node.style.transform = '';
      });
    }

    /* Hold the lane's height while cards are leaving.
     *
     * An exiting card is taken OUT OF FLOW (position:absolute, below) so it can
     * animate away without shoving its neighbours. On the desktop board that is
     * free: the lane scrolls inside a fixed grid cell, so the cell's height
     * never depended on the cards. In the phone shell the lane GROWS WITH ITS
     * CARDS by design (mobile_shell.css: height auto, overflow visible, first
     * five shown) - so the moment several cards go absolute together, the
     * section collapses to nothing and everything below it leaps up the page.
     *
     * Measured on an upright iPad against the live NFL slate, and reproduced
     * here: the lane fell to 61px while still holding 8 cards, and the Dream
     * Team jumped 440px up the page on the event, then back (Xav, 2026-09-13).
     *
     * Counted, not a boolean: several cards exit together when the feed trims,
     * and the first one to finish must not release the floor while the rest are
     * still in the air. */
    _holdFloor() {
      const host = this.el;
      if (!host) return;
      if (!host._vfExits) {
        host._vfExits = 0;
        host.style.minHeight = host.offsetHeight + 'px';
      }
      host._vfExits++;
    }

    _releaseFloor() {
      const host = this.el;
      if (!host || !host._vfExits) return;
      if (--host._vfExits > 0) return;
      host._vfExits = 0;
      // Next frame: let the remaining cards finish settling into their new
      // positions before the floor goes, so the lane resizes once, smoothly,
      // instead of snapping while a FLIP is still running.
      requestAnimationFrame(() => {
        if (!host._vfExits) host.style.minHeight = '';
      });
    }

    _exit(node) {
      if (REDUCE) { node.remove(); return; }
      const top = node.offsetTop, left = node.offsetLeft, w = node.offsetWidth;
      // A row with no measured width was never laid out - the lane is on a
      // hidden tab, or the node entered and left inside one frame. Pinning it
      // at `width: 0` then animating is worse than not animating: the row
      // collapses to a 27px sliver, every label inside it re-wraps against a
      // dead column, and the whole thing fades from a shape the user never
      // saw. Found by a phone sweep that reported the wire's scoreline as 31%
      // truncated - it was, on rows sitting at opacity 0. Nothing to animate
      // out, so take it out.
      if (!w) { node.remove(); return; }
      // A lane that scrolls (Broadcast's Wire) takes a leaving card out at
      // once: faded out of flow it sat on the card that took its place - a
      // goal's rows folding into its play card printed both on one spot
      // (audit v2 run 3, WIRE-10, 2-4 of 60 samples at 1440 / 1920)
      const oy = getComputedStyle(this.el).overflowY;
      if (oy === 'auto' || oy === 'scroll') { node.remove(); return; }
      this._holdFloor();
      // a card still in its entrance (the is-fresh ring animation, a pending
      // stagger, the enter transition's end handler) kept full opacity while it
      // left - two cards printed on one spot (WIRE-10): it stops entering now
      node.__vfExiting = true;
      node.classList.remove('is-fresh');
      node.style.animation = 'none';
      node.style.position = 'absolute';
      node.style.top = top + 'px';
      node.style.left = left + 'px';
      node.style.width = w + 'px';
      node.style.margin = '0';
      node.style.pointerEvents = 'none';
      node.style.zIndex = '0';
      void node.offsetHeight;
      // gone fast: a leaving card sits out of flow at its old place, and a
      // burst brought the next one in under it - 4-5 cards overprinted at
      // 1920 (audit v2 run 3, WIRE-10). Out in a blink, not a slow fall.
      node.style.transition = 'opacity .12s ease-out, transform .2s var(--vidi-ease-push)';
      node.style.opacity = '0';
      node.style.transform = 'translateY(14px) scale(.97)';
      // transitionend AND the 520ms belt-and-braces timer can both fire; the
      // floor is reference-counted, so releasing twice would drop it early.
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        node.remove();
        this._releaseFloor();
      };
      node.addEventListener('transitionend', (e) => { if (e.propertyName === 'opacity') done(); }, { once: true });
      setTimeout(done, 260);
    }

    clear() {
      if (this.el) {
        this.el.textContent = '';
        // Nothing is animating any more, so the held floor would strand the
        // lane at its old height with no cards in it.
        this.el._vfExits = 0;
        this.el.style.minHeight = '';
      }
      this.nodes.clear();
    }
  }

  /* ── Card builders ─────────────────────────────────────────────────── */

  /* ── THE LEDGER (Xav 2026-10-15, mock K-A on the design canvas) ────────
   * One grid for every play on the Wire: the player's own crest · his name
   * and what he did · the points · his running total. A one-player play and
   * a linked one (a basket and its assist, a turnover and its steal) are the
   * SAME rows, so the points run in one straight column down the panel. On
   * top, one quiet line: the play's sentence on the left, its clock and the
   * game score on the right. Replaces three layouts for one thing - a boxed,
   * indented group; a different single row; a score flush-left under both -
   * and the home team's crest on every row of a group. */
  function _ledgerScore(ev) {
    const gs = ev.game_state;
    const score = (gs && gs.a)
      ? esc(gs.a) + ' <b>' + esc(gs.as) + '</b>–<b>' + esc(gs.hs) + '</b> ' + esc(gs.h) : '';
    const clk = ev.gameTime ? esc(otTime(ev.gameTime)) : '';
    return [clk, score].filter(Boolean).join(' · ');
  }
  // "Giannis Antetokounmpo defensive rebound" on a row that already says
  // Giannis Antetokounmpo: the sentence keeps only what he did.
  function _ledgerSay(text, name) {
    let t = String(text || '').trim();
    const nm = String(name || '').trim();
    if (nm && t.toLowerCase().startsWith(nm.toLowerCase() + ' ')) t = t.slice(nm.length + 1);
    // the feed's cleaner already drops the name on some sports, leaving
    // "offensive rebound" / "makes tip shot" - every sentence starts upper case
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  function _ledgerCrest(team, rawLogo) {
    const logo = (typeof window.lsSmallLogo === 'function') ? window.lsSmallLogo(rawLogo || '') : (rawLogo || '');
    const t = esc((team || '').toUpperCase());
    return logo
      ? '<img class="vcx-logo vcl-crest" src="' + esc(logo) + '" alt="" data-team="' + t + '">'
      : '<span class="vcx-logo vcx-logo--txt vcl-crest">' + t + '</span>';
  }
  // One case for every play name: "Shot on Goal", "2-Pointer", "TOUCHDOWN"
  // and "Run · 5 yds" came from four rule tables in four cases. Sentence case,
  // keeping short all-caps words (TD, OT, PP) and anything with a digit.
  function _ledgerLabel(label) {
    const words = String(label || '').trim().split(/(\s+)/);
    let first = true;
    return words.map(w => {
      if (/^\s+$/.test(w) || !/[a-z]/i.test(w)) return w;
      if (/^[A-Z]{2,3}$/.test(w) && w !== 'RUN' && w !== 'HIT' && w !== 'WIN') { first = false; return w; }
      const lw = w.toLowerCase();
      const out = first ? lw.charAt(0).toUpperCase() + lw.slice(1) : lw;
      first = false;
      return out;
    }).join('');
  }
  function _ledgerRow(m, helpers, opts) {
    const dec = helpers.decimals ? helpers.decimals() : 2;
    const usfx = helpers.unitSfx ? helpers.unitSfx() : '';
    const f = Number(m.fpts) || 0, t = Number(m.total);
    const hasT = m.rule_id != null && m.total != null && !isNaN(t);
    const corr = !!m.is_correction;
    const cls = (corr ? 'corr ' : '') + (f > 0 ? 'pos' : f < 0 ? 'neg' : 'zero');
    const raw = m.team_logo || (helpers.logoFor ? helpers.logoFor(m.team) : '') || '';
    return '<div class="vcl-r' + (m.own ? ' is-mine' : '') + '">' +
      _ledgerCrest(m.team, raw) +
      '<span class="vcl-who"><span class="vcl-name ls-player-name" title="' + esc(m.name || '') + '">' + esc(m.name || '') + '</span>' +
        (m.label ? '<span class="vcl-what">' + esc(_ledgerLabel(m.label)) + '</span>' : '') +
        (m.pos ? '<span class="vcx-pos">' + esc(m.pos) + '</span>' : '') +
        // in your lineups: a count, never points x lineups ("in 2/3", "yours")
        (m.own ? '<span class="vcl-own" title="In your lineups">' + esc(m.own === 'yours' ? 'yours' : 'in ' + m.own) + '</span>' : '') +
      '</span>' +
      // a play worth nothing to you shows no number (audit run 3, LIVE-6)
      '<span class="vcl-pts ' + cls + '">' + (f || corr ? _signed(f, dec) + usfx : '') + '</span>' +
      '<span class="vcl-tot" title="Player running total">' + (hasT ? _num(t, dec) + usfx : '') + '</span>' +
    '</div>';
  }
  function _ledgerCard(n, ev, rows, say, helpers, extra) {
    const ctx = _ledgerScore(ev);
    n.className = 'vc vcl' + (extra ? ' ' + extra : '');
    n.innerHTML =
      ((say || ctx) ? '<div class="vcl-meta"><span class="vcl-say"' + (say ? ' title="' + say + '"' : '') + '>' + (say || '') + '</span>' +
        (ctx ? '<span class="vcl-ctx">' + ctx + '</span>' : '') + '</div>' : '') +
      rows.map(m => _ledgerRow(m, helpers)).join('');
    wireLogoFallback(n);
    return n;
  }

  // Terse one-line wire row: time · team · name · play · +fpts.
  function buildWireCard(ev, helpers) {
    const n = document.createElement('div');

    // One goal, one row (live.js _groupPlays): the play's head - score,
    // clock, crest, GOAL and its sentence - then a line per player in it
    // with his points from the play and his running total. No sum.
    if (ev.event_type === 'play_group') {
      // every player keeps his OWN crest - a BKN turnover under a MIA steal
      // wore the home team's badge for both (Xav 2026-10-15)
      const ms = (ev.members || []).map(m => Object.assign({ team: ev.team, team_logo: ev.team_logo }, m));
      const say = ev.desc ? esc(_ledgerSay(helpers.descClean(ev), '')) : esc(_ledgerLabel(ev.tag || ''));
      return _ledgerCard(n, ev, ms, say, helpers, 'vc--group');
    }

    // The folded stat corrections of a finished slate (live.js
    // _foldCorrections): one quiet row, the list in its tooltip.
    if (ev.event_type === 'corr_sum') {
      const f = Number(ev.fpts) || 0;
      n.className = 'vc vc2 vc--corr vc--sum';
      n.setAttribute('data-tip', ev.tip || '');
      n.tabIndex = 0;
      // the count in the tag, so a narrow lane can drop the sentence and keep
      // one line (audit v2 run 3, SPACE-13)
      n.innerHTML = '<div class="vcx-sum"><span class="vcx-tag">' + esc(String(ev.n)) + ' stat corrections</span>' +
        '<span class="vcx-sum-n">after the final whistle</span>' +
        '<b class="vcx-fpts ' + (f < 0 ? 'neg' : 'pos') + '">' + _signed(f, helpers.decimals ? helpers.decimals() : 2) + ' net</b></div>';
      return n;
    }

    // Score-change band — the SCORE FLASH, built in the stream board's language
    // (static/js/tv.js showEvent, e.et === "score"): the scoreline on ONE centred
    // line — [crest] AWY 2 - 4 HME [crest] — with the side that just scored lit
    // and pulsing, and the tag mirrored left and right so the scoreline sits dead
    // centre however wide the wire gets.
    //
    // It replaces two stacked rows that read as a table, not an event. The board
    // and the dashboard now flash the same thing, which is the point: a viewer
    // arriving from the stream should recognise the app.
    if (ev.event_type === 'score') {
      const side = ev.score_side === 'home' ? 'home' : 'away';
      // Crest with the white "coin" behind it — team logos are drawn for a white
      // ground, so the dark-navy ones (NYY/SEA/TB/DET) vanish without it.
      const crest = (which) => {
        const logo = which === 'home' ? ev.home_logo : ev.away_logo;
        return logo
          ? '<img class="vcs-logo" src="' + esc(logo) + '" alt="" data-hide-on-error>'
          : '<span class="vcs-logo vcs-logo--empty"></span>';
      };
      const abbr = (which) => {
        const a = which === 'home' ? ev.home_abbr : ev.away_abbr;
        const n = which === 'home' ? ev.home_name : ev.away_name;
        return '<span class="vcs-ab' + (side === which ? ' is-scorer' : '') + '">' +
               esc((a || n || '').toUpperCase()) + '</span>';
      };
      const num = (which) => {
        const v = which === 'home' ? ev.home_score : ev.away_score;
        return '<span class="vcs-cs' + (side === which ? ' is-scorer' : '') + '">' +
               esc(v != null ? v : 0) + '</span>';
      };
      // "T7" / "B9" earn the triangle the board gives them — ▲ top, ▼ bottom.
      // Anything else (a clock, a quarter) prints as-is.
      // a fifth quarter is overtime, on every row (v2 run 2, LIVE-5: "Q5 11:02")
      const clk = otTime(ev.gameTime);   // the one OT rewrite (CODE-14)
      const m = /^([TB])(\d.*)$/.exec(clk);
      const inn = clk
        ? (m ? '<span class="vcs-clk"><span class="vcs-tri vcs-tri--' +
                 (m[1] === 'T' ? 'up">▲' : 'dn">▼') + '</span>' + esc(m[2]) + '</span>'
             : '<span class="vcs-clk">' + esc(clk) + '</span>')
        : '';
      const lead = '<div class="vcs-lead">' +
                     '<span class="vcs-tag">' + esc(ev.headline || 'SCORE UPDATE') + '</span>' +
                     inn +
                   '</div>';
      n.className = 'vc vc--score';
      n.innerHTML =
        lead +
        '<div class="vcs-line">' +
          crest('away') + abbr('away') +
          '<span class="vcs-sc">' + num('away') +
            '<span class="vcs-dash">-</span>' + num('home') + '</span>' +
          abbr('home') + crest('home') +
        '</div>' +
        lead;
      n.querySelectorAll('[data-hide-on-error]').forEach((im) => {
        im.onerror = function () { this.style.display = 'none'; };
      });
      return n;
    }

    // Non-scoring status blip (start / oncourt / end / offcourt / ot).
    if (ev.event_type && ev.event_type !== 'achievement' && ev.event_type !== 'big_play') {
      const on = ev.event_type === 'start' || ev.event_type === 'oncourt';
      const off = ev.event_type === 'end' || ev.event_type === 'offcourt';
      // "Top 9th" (gameTime) already states the inning, so drop a redundant
      // "Inning 9" desc; keep meaningful ones (Game Started / Ended).
      const sdesc = ev.desc || '';
      const showDesc = sdesc && !/^inning\s+\d+$/i.test(sdesc.trim());
      n.className = 'vc vc--status vc--match';
      n.innerHTML =
        (ev.gameTime ? '<span class="vc-time">' + esc(otTime(ev.gameTime)) + '</span>' : '') +
        // Shape AND colour, plus a name for a screen reader: filled disc =
        // came on, hollow ring = went off, square = extra time. Colour alone
        // was the only difference, which a red/green-blind reader cannot see
        // and which says nothing at all to a screen reader (WCAG 1.4.1, Level
        // A - audit, live slate).
        '<span class="vc-dot ' + (on ? 'vc-dot--on' : off ? 'vc-dot--off' : 'vc-dot--ot') +
          '" role="img" aria-label="' +
          (on ? 'Started' : off ? 'Ended' : 'Extra time') + '"></span>' +
        // Matchup (e.g. "CWS @ CLE") — carried on ev.name; without it a bare
        // "Top 9th" is ambiguous across a multi-game slate.
        (ev.name ? '<span class="vc-name vc-name--status" title="' + esc(ev.name) + '">' + esc(ev.name) + '</span>' : '') +
        (showDesc ? '<span class="vc-desc">' + esc(helpers.descClean(ev)) + '</span>' : '');
      return n;
    }

    // A play row, in the ledger (see _ledgerCard above).
    const fpts = Number(ev.fpts) || 0;
    const corr = ev.is_correction;
    // Show the narrative only when it's more than the terse label repeated —
    // a pitcher's "Strikeout" row has no borrowed sentence, because the
    // narrative belongs to the HITTER ("grounds out to short").
    const narrative = (ev.desc && ev.desc !== ev.label) ? helpers.descClean(ev) : '';
    /* THE COUNTERPART, when there is no sentence to show. The poller resolves
       it (statsapi playByPlay carries matchup.batter / matchup.pitcher and the
       scored event is correlated to the current at-bat), event_detection.js
       carries it through as `vs`. Absent is a valid answer: the correlation
       upstream only fires on the LATEST at-bat, so a baserunner's Run or SB
       gets no counterpart rather than a wrong one. */
    const versus = (!narrative && ev.vs) ? 'v ' + esc(ev.vs) : '';
    const big = ev.event_type === 'big_play' || ev.event_type === 'achievement';
    // the sentence without his name (the row under it says who), else who he
    // did it against, else nothing - the row's own label says what it was
    const say = narrative ? esc(_ledgerSay(narrative, ev.name)) : versus;
    const extra = (corr ? 'vc--corr' : fpts > 0 ? 'vc--pos' : fpts < 0 ? 'vc--neg' : 'vc--flat') + (big && !corr ? ' vc--big' : '');
    return _ledgerCard(n, ev, [{
      name: ev.name, team: ev.team, team_logo: ev.team_logo, pos: ev.pos, label: ev.label,
      fpts: ev.fpts, total: ev.total, rule_id: ev.rule_id, own: ev._own, is_correction: corr,
    }], say, helpers, extra);
  }

  // A crest that fails to load becomes the team's code, never a blank tile
  // (audit v2 run 3, WIRE-13).
  function wireLogoFallback(n) {
    n.querySelectorAll('img.vcx-logo[data-team]').forEach((im) => {
      im.onerror = function () {
        const t = document.createElement('span');
        t.className = 'vcx-logo vcx-logo--txt';
        t.textContent = this.getAttribute('data-team') || '';
        this.replaceWith(t);
      };
    });
  }

  // Rich lingering moment card: team · name · big +fpts / label · context.
  function buildMomentCard(ev, helpers) {
    const n = document.createElement('div');
    n.className = 'mc' + (ev._fill ? ' mc--fill' : '');
    // the play's own points (a goal +8.50), not everything else of his that
    // landed in the same poll (slate_live keeps play_fpts through the merge)
    const fpts = Number(ev.play_fpts != null ? ev.play_fpts : ev.fpts) || 0;
    const dec  = helpers.decimals ? helpers.decimals() : 2;
    const usfx = helpers.unitSfx ? helpers.unitSfx() : '';
    const label = ev.play_label || ev.label || helpers.descClean(ev) || '';
    // Game-context line — score/period stamped server-side at write-time
    // (game_state = {a,as,h,hs,inn}). Degrades to the game clock if absent.
    const gs = ev.game_state;
    let ctx = '';
    if (gs && gs.a) {
      const inn = gs.inn ? ' <span class="arw">▲</span> ' + esc(gs.inn) : '';
      ctx = '<span class="mc-ctx">' + esc(gs.a) + ' ' + esc(gs.as) +
            ' · ' + esc(gs.h) + ' ' + esc(gs.hs) + inn + '</span>';
    } else if (ev.gameTime) {
      ctx = '<span class="mc-ctx">' + esc(otTime(ev.gameTime)) + '</span>';
    }
    n.innerHTML =
      '<div class="mc-top">' +
        helpers.teamChip(ev) +
        '<span class="mc-name ls-player-name" title="' + esc(ev.name) + '">' + esc(ev.name) + '</span>' +
        (ev.pos ? '<span class="vcx-pos">' + esc(ev.pos) + '</span>' : '') +
        '<span class="mc-fpts">' + _signed(fpts, dec) + usfx + '</span>' +
      '</div>' +
      '<div class="mc-bot">' +
        '<span class="mc-label">' + (ev._fill ? '★ ' : '') + esc(label) + '</span>' +
        ctx +
      '</div>' +
      // the play in words - who set it up ("wrist shot, from Gritsyuk and
      // Hughes"). Broadcast's Big Moments shows this instead of the points
      // (Xav 2026-10-10: the moment is the goal, not its fantasy points);
      // other boards keep their card and do not show it.
      (ev._say ? '<div class="mc-say">' + esc(ev._say) + '</div>' : '');
    return n;
  }

  /* ── The manager — owns both lanes + shared helpers ────────────────── */
  class VidiFeed {
    constructor(opts) {
      opts = opts || {};
      const h = opts.helpers || {};
      this.helpers = {
        // Strip the leading player-name prefix the data source embeds
        // (MLB statsapi: "Jackson Holliday singles…" → "singles…") so the
        // card doesn't read the name twice. Mirrors vidi.html _descClean.
        descClean: h.descClean || ((ev) => {
          const desc = (ev.desc || '').trim();
          const name = (ev.name || '').trim();
          if (name && desc && desc.toLowerCase().startsWith(name.toLowerCase())) {
            return desc.slice(name.length).replace(/^[\s.,;:-]+/, '');
          }
          return desc;
        }),
        logoFor:   h.logoFor   || (() => ''),
        colorFor:  h.colorFor  || (() => ''),
        textFor:   h.textFor   || (() => ''),
        indiv:     h.indiv     || (() => false),
        decimals:  h.decimals  || (() => 2),   // format decimals trait (2 fantasy / 0 single-stat)
        unitSfx:   h.unitSfx   || (() => ''),   // " HR" for single-stat, "" for standard
        shortName: h.shortName || ((name) => (String(name || '').trim().split(/\s+/).pop() || '').slice(0, 3).toUpperCase()),
        // Identity token — mode-agnostic .tk markup; the ttm-<X> class on the
        // dashboard root drives the mode. Sport-aware: team logo+abbrev, or
        // player photo + 3-char surname for individual sports.
        teamChip:  (ev) => {
          const indiv = this.helpers.indiv();
          const url   = indiv ? (ev.player_photo || '') : (ev.team_logo || (this.helpers.logoFor(ev.team) || ''));
          const label = esc(indiv ? this.helpers.shortName(ev.name) : (ev.team || '').toUpperCase());
          const color = indiv ? '' : (this.helpers.colorFor(ev.team) || '');
          const style = color ? ' style="--tk-c:' + esc(color) + ';--tk-tc:' + esc(this.helpers.textFor(color) || '') + '"' : '';
          const img   = url ? '<img class="tk-logo" src="' + esc(url) + '" alt="" onerror="this.style.display=\'none\'">' : '';
          return '<span class="tk' + (url ? '' : ' tk--nologo') + '"' + style + '>' + img +
                 '<span class="tk-abbr">' + label + '</span></span>';
        },
      };
      this.wire = null;
      this.moments = null;
      this._last = null;
      this._ro = null;
      this._roRaf = 0;
    }

    // Mount both lanes at once — the combined `vidi` widget (both lanes in one
    // panel). The split widgets call mountWire / mountMoments independently.
    mount(wireEl, momentsEl) {
      if (wireEl)     this.mountWire(wireEl);
      if (momentsEl)  this.mountMoments(momentsEl);
    }

    mountWire(el) {
      this.wire = new Lane(el, (ev) => buildWireCard(ev, this.helpers), WIRE_ROW);
      this._observe(el);
    }

    mountMoments(el) {
      this.moments = new Lane(el, (ev) => buildMomentCard(ev, this.helpers), MOMENT_ROW);
      this._observe(el);
    }

    // Re-run fill-to-height on resize (coalesced to one frame). One observer per
    // mounted lane element; observers on later-detached elements are harmless.
    _observe(el) {
      if (!(window.ResizeObserver && el)) return;
      // one observer per lane element: a remount (tab switch) made another,
      // never disconnected (audit v2 run 3, CODE-13)
      if (el.__vfRo) el.__vfRo.disconnect();
      const ro = new ResizeObserver(() => {
        if (this.wire)    this.wire._cap = null;      // re-measure once, here
        if (this.moments) this.moments._cap = null;
        if (this._roRaf) return;
        this._roRaf = requestAnimationFrame(() => { this._roRaf = 0; if (this._last) this.render(this._last); });
      });
      ro.observe(el);
      el.__vfRo = ro;
    }

    // True once AT LEAST one lane is mounted — the wire and moments widgets can
    // live on the board independently (or together via the combined widget).
    mounted() { return !!((this.wire && this.wire.el) || (this.moments && this.moments.el)); }

    // lanes: { wire: [...newest-first], moments: [...newest-first] }. Paints
    // whichever lanes are actually mounted, so a board with only one is fine.
    render(lanes, instant) {
      if (!this.mounted()) return;
      lanes = lanes || {};
      this._last = lanes;
      if (this.wire && this.wire.el) {
        this.wire.render((lanes.wire || []).slice(0, this.wire.capacity()), instant);
        // how many plays the lane holds that are not on screen ("+N earlier")
        this.wireMore = this.wire.shown != null ? Math.max(0, (lanes.wire || []).length - this.wire.shown) : 0;
      }
      if (this.moments && this.moments.el) this.moments.render((lanes.moments || []).slice(0, this.moments.capacity()), instant);
    }

    clear() {
      if (this.wire) this.wire.clear();
      if (this.moments) this.moments.clear();
    }
  }

  window.VidiFeed = VidiFeed;
})();
